// Stack.js
import React, { useState, useEffect } from 'react';

const stackGroups = {
 "AI & ML": [
  {name: 'OpenAI API', badge: 'ai'},               // More specific than "OpenAI" alone
  {name: 'GPT-4 Vision', badge: 'ai'},
  {name: 'Whisper (ASR)', badge: 'ai'},            // Clarifies it's Automatic Speech Recognition
  {name: 'Hugging Face Transformers', badge: 'ai'},// Shows you're using their core library
  {name: 'LangChain', badge: 'ai'},                // Important for agent + toolchains
  {name: 'LLaMA / Ollama', badge: 'ai'},           // Your hybrid/local AI deserves a mention
  {name: 'RAG Architecture', badge: 'ai'},
  {name: 'ChromaDB', badge: 'ai'},                 // Full name — makes it easier to recognize
  {name: 'FAISS', badge: 'ai'},
  {name: 'LoRA / PEFT', badge: 'ai'},              // Groups LoRA with Hugging Face's tuning lib
  {name: 'PyTorch', badge: 'ai'},
  {name: 'TensorFlow / Keras', badge: 'ai'},
  {name: 'Mathploit (ML Puzzles)', badge: 'ai'}    // Fine as a flex — niche but fun
],
  "Software Development": [
    {name: 'Python', badge: 'dev'},
    {name: 'C#', badge: 'dev'},
    {name: 'JavaScript', badge: 'dev'},
    {name: 'Swift', badge: 'dev'},
    {name: 'React', badge: 'dev'},
    {name: 'Flask', badge: 'dev'},
    {name: 'Three.js', badge: 'dev'},
    {name: 'WebXR/AR.js', badge: 'dev'},
    {name: 'TypeScript', badge: 'dev'},    
    {name: 'C++', badge: 'dev'},               
    {name: 'VB.NET', badge: 'dev'},            
    {name: 'Pine Script', badge: 'dev'},       
    {name: 'Objective-C', badge: 'dev'},      
    {name: 'HTML/CSS', badge: 'dev'},         
    {name: 'PHP', badge: 'dev'},               
    {name: 'Shell/Bash/CLI', badge: 'dev'},   
    {name: 'Batch Scripting', badge: 'dev'},
    {name: 'RPGLE / CL / DDS', badge: 'enterprise'}   
  ],
"Cloud & Data": [
  {name: 'AWS (EC2, S3, RDS, Route 53, Beanstalk)', badge: 'cloud'},
  {name: 'Azure (VMs, Managed DBs, Azure ML)', badge: 'cloud'},
  {name: 'Google Cloud (Compute, Firebase)', badge: 'cloud'},
  {name: 'Docker', badge: 'cloud'},
  {name: 'Kubernetes', badge: 'cloud'},
  {name: 'SQL Server', badge: 'cloud'},
  {name: 'REST APIs / JSON', badge: 'cloud'}
],
"Integration & Automation": [
  {name: 'REST APIs / Webhooks', badge: 'integration'},    // Adds completeness
  {name: 'RPA (UiPath / Power Automate)', badge: 'integration'}, // Shows you're tool-agnostic
  {name: 'SAP Integration (RFC / BAPI)', badge: 'integration'},  // Shows low-level SAP connection skills
  {name: 'Microsoft Power Platform', badge: 'integration'}, // Combines Power BI, Apps, Automate under one badge
  {name: 'ETL Pipelines (SSIS / Custom)', badge: 'integration'}, // Specifies tech + custom skills
  {name: 'Data Connectors (CSV, JSON, SQL, API)', badge: 'integration'}, // Flexes your universal glue logic
  {name: 'Robotic Process Automation (RPA)', badge: 'integration'},
  {name: 'Low-code / No-code Platforms', badge: 'integration'},
  {name: 'AI-Driven Automation', badge: 'integration'}
],
  "Emerging Tech": [
    {name: 'Quantum Computing', badge: 'emerging'},
    {name: 'IBM Q', badge: 'emerging'},
    {name: 'Blockchain', badge: 'emerging'},
    {name: 'WebXR', badge: 'emerging'},
    {name: 'Generative AI', badge: 'emerging'},
    {name: 'Edge AI', badge: 'emerging'},    
    {name: 'Neuromorphic Computing', badge: 'emerging'}, 
    {name: 'Digital Twins', badge: 'emerging'},     
    {name: 'Spatial Computing', badge: 'emerging'},  
    {name: 'AI in Drug Discovery', badge: 'emerging'}, 
    {name: 'Mixed Reality (MR)', badge: 'emerging'}, 
    {name: 'AI-Powered Robotics', badge: 'emerging'}, 
  ],
 "Development Environments": [
  {name: 'VS Code', badge: 'ide'},
  {name: 'Visual Studio (Pro)', badge: 'ide'},
  {name: 'Xcode', badge: 'ide'},
  {name: 'PyCharm', badge: 'ide'},
  {name: 'JupyterLab', badge: 'ide'},
  {name: 'Google Colab', badge: 'ide'},
  {name: 'Anaconda Navigator', badge: 'ide'},
  {name: 'Git / GitHub', badge: 'ide'},
  {name: 'Postman', badge: 'ide'},
  {name: 'Terminal / Bash / CLI', badge: 'ide'}
],
"IoT & Hardware": [
  {name: 'ESP32', badge: 'iot'},
  {name: 'Raspberry Pi', badge: 'iot'},
  {name: 'Arduino', badge: 'iot'},
  {name: 'C++ for Embedded', badge: 'iot'},
  {name: 'PlatformIO', badge: 'iot'},
  {name: 'Arduino Sketches', badge: 'iot'},
  {name: 'Sensors & Actuators', badge: 'iot'},
  {name: 'Camera Modules', badge: 'iot'},
  {name: 'Microcontroller Programming', badge: 'iot'},
  {name: 'IoT Protocols (MQTT, HTTP)', badge: 'iot'}
]
};

export default function Stack() {
  const [activeTab, setActiveTab] = useState("AI & ML");
  const categories = Object.keys(stackGroups);

  return (
    <div className="container py-5">
      <h2 className="text-center mb-4">
        <span role="img" aria-label="gear">⚙️</span> Skills & Technologies
      </h2>

      {/* Tabs Navigation */}
      <ul className="nav nav-tabs justify-content-center mb-4" role="tablist">
        {categories.map((category) => (
          <li className="nav-item" key={category}>
            <button
              className={`nav-link ${activeTab === category ? 'active' : ''}`}
              onClick={() => setActiveTab(category)}
              type="button"
            >
              {category}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {categories.map((category) => (
          <div
            key={category}
            className={`tab-pane fade ${activeTab === category ? 'show active' : ''}`}
          >
            {activeTab === category && (
              <div className="row justify-content-center">
                <div className="col-md-10 col-lg-8">
                  <div className="card shadow-sm" style={{ minHeight: '350px' }}>
                    <div className="card-body p-4">
                      <div className="skills-grid-tabs">
                        {stackGroups[category].map((skill, i) => (
                          <span key={i} className="tech-badge-uniform-tab">
                            {skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary text */}
      <div className="row mt-5">
        <div className="col-12">
          <div className="text-center p-4">
            <p className="lead text-muted mb-0" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
              From concept to reality—I adapt and use the right tools to get the job done, across code and hardware.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}